import React, { useEffect, useState } from 'react';

import { DataSeriesEntry } from '@/modules/calendar/model/calendar/data-series-entry';
import { DataSeriesEntryForecast } from '@/modules/calendar/model/calendar/data-series-entry-forecast';
import { FormattingUtil } from '@/modules/calendar/util/formatting-util';
import ForecastEditor from '@/modules/calendar/components/forecast-editor';

import styles from '@/modules/calendar/components/data-calendar/rows/data-row-entry.module.css';
import { readerService } from '@/modules/calendar/services';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { useSessionIdentity } from '@/core/auth/export';

interface DataRowEntryProps {
  dataSeriesEntry: DataSeriesEntry;
  date: string;
  reportingPeriod?: string;
  isNext: boolean;
  event: NewsEvent;
}

function hasValue(value: string | number | null | undefined): boolean {
  return value !== null && value !== undefined && value !== '';
}

export default function DataRowEntry({
  dataSeriesEntry,
  date,
  reportingPeriod,
  isNext,
  event,
}: DataRowEntryProps) {
  const identity = useSessionIdentity();

  const getDiffClass = (diff: number | null) => {
    let diffClass: string | undefined;

    if (diff === null) diffClass = '';
    else if (diff > 0) diffClass = 'diff-positive';
    else diffClass = 'diff-negative';

    return diffClass;
  };

  const [diff, setDiff] = useState(dataSeriesEntry.diff());
  const [diffClass, setDiffClass] = useState(getDiffClass(diff));

  const hasActual = hasValue(dataSeriesEntry.actual);

  const refreshEntry = (forecast?: DataSeriesEntryForecast) => {
    if (!forecast) dataSeriesEntry.removeUserForecast();
    else dataSeriesEntry.addForecast(forecast);

    setDiff(dataSeriesEntry.diff());
    setDiffClass(getDiffClass(dataSeriesEntry.diff()));
  };

  useEffect(() => {
    setDiff(dataSeriesEntry.diff());
    setDiffClass(getDiffClass(dataSeriesEntry.diff()));
  }, [dataSeriesEntry, dataSeriesEntry.actual]);

  return (
    <React.Fragment>
      <td
        className={
          readerService.isReading(dataSeriesEntry.dataSeriesId, date)
            ? 'text-body-secondary'
            : 'text-text-dark'
        }
      >
        {dataSeriesEntry.display}
        <small className={'text-muted d-container-none d-container-sm-inline'}>
          {' '}
          {reportingPeriod}
        </small>
      </td>
      <td>
        <span className={'text-muted d-container-none d-container-sm-inline'}>
          {dataSeriesEntry.scale} {dataSeriesEntry.type}
        </span>
      </td>
      <td
        className={`${isNext ? 'border-start bg-quaternary' : ''}`}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <ForecastEditor
          event={event}
          entry={dataSeriesEntry}
          date={date}
          onChange={forecast => refreshEntry(forecast)}
          identity={identity}
        />
      </td>
      <td
        className={`${isNext ? 'bg-quaternary' : ''} ${
          !hasActual && !dataSeriesEntry.hasProvider && styles.noProvider
        }`}
        key={'actual-' + dataSeriesEntry}
      >
        {hasActual && (
          <strong>
            {FormattingUtil.formatNumber(dataSeriesEntry.actual, dataSeriesEntry.precision)}
          </strong>
        )}
        {!hasActual && !dataSeriesEntry.hasProvider && <div className={styles.noProvider} />}
      </td>
      <td className={`${isNext ? 'bg-quaternary' : ''}`} key={'diff-' + diff + diffClass}>
        <span className={diffClass}>
          {FormattingUtil.formatNumber(diff, dataSeriesEntry.precision)}
        </span>
      </td>
      <td className={`${isNext ? 'border-end bg-quaternary' : ''}`}>
        <span className={'text-text-dark'}>
          {FormattingUtil.formatNumber(dataSeriesEntry.previous, dataSeriesEntry.precision)}
        </span>
        {dataSeriesEntry.hasPreviousRevision && (
          <span className={`text-body`}>
            {' '}
            /{' '}
            {FormattingUtil.formatNumber(
              hasValue(dataSeriesEntry.previousRevisionEntry?.actual)
                ? dataSeriesEntry.previousRevisionEntry?.actual
                : '__',
              dataSeriesEntry.precision,
            )}
          </span>
        )}
      </td>
    </React.Fragment>
  );
}
